import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

export function useScrollPosition(target?: Element | null): { x: number; y: number } {
  const [pos, setPos] = useState({
    x: typeof window !== 'undefined' ? window.scrollX : 0,
    y: typeof window !== 'undefined' ? window.scrollY : 0
  });
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const el = target ?? window;
    const handler = () => {
      if (el === window) setPos({ x: window.scrollX, y: window.scrollY });
      else setPos({ x: (el as Element).scrollLeft, y: (el as Element).scrollTop });
    };
    handler();
    el.addEventListener('scroll', handler, { passive: true });
    return () => el.removeEventListener('scroll', handler);
  }, [target]);
  return pos;
}

export function useIntersectionObserver(
  ref: RefObject<Element | null>,
  options: IntersectionObserverInit = {}
): IntersectionObserverEntry | null {
  const [entry, setEntry] = useState<IntersectionObserverEntry | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(([e]) => setEntry(e ?? null), options);
    observer.observe(el);
    return () => observer.disconnect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, options.root, options.rootMargin, options.threshold]);
  return entry;
}

export interface ElementSize {
  width: number;
  height: number;
}

export function useElementSize(ref: RefObject<Element | null>): ElementSize {
  const [size, setSize] = useState<ElementSize>({ width: 0, height: 0 });
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => {
      const e = entries[0];
      if (e) setSize({ width: e.contentRect.width, height: e.contentRect.height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return size;
}

export function useScrollLock(active = true): void {
  const stored = useRef<string | null>(null);
  useEffect(() => {
    if (!active || typeof document === 'undefined') return undefined;
    stored.current = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = stored.current ?? '';
    };
  }, [active]);
}

export function useScrollToBottom(ref: RefObject<HTMLElement | null>, deps: unknown[] = []): () => void {
  const scroll = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [ref]);
  useEffect(() => {
    scroll();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scroll, ...deps]);
  return scroll;
}
